

import { injectable, inject, Container } from 'inversify';
import { ClientTypes, IModelService, Schemas, ITextEditorModel, IFileLoadReason, IModeService } from '@workbench-stack/core';
import { ClientTypes as FilesClientTypes, IUntitledEditorService, IFileService, ITextFileService, ITextFileEditorModel } from '@files-stack/core';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';
import { ReferenceCollection, ImmortalReference, IReference, IDisposable, toDisposable } from '@vscode-alt/monaco-editor/esm/vs/base/common/lifecycle';
import { ITextModel } from '@vscode-alt/monaco-editor/esm/vs/editor/common/model';
import { IEditorModel } from '@vscode-alt/monaco-editor/esm/vs/platform/editor/common/editor';
import { ITextModelService, ITextModelContentProvider } from '@vscode-alt/monaco-editor/esm/vs/editor/common/services/resolverService';
import { first } from '@vscode-alt/monaco-editor/esm/vs/base/common/async';
import { ResourceEditorModel } from '../../core/models';
import * as network from '@vscode-alt/monaco-editor/esm/vs/base/common/network';

export interface IResolvedTextEditorModel extends ITextEditorModel {

	/**
	 * Same as ITextEditorModel#textEditorModel, but never null.
	 */
	readonly textEditorModel: ITextModel;
}



@injectable()
class ResourceModelCollection extends ReferenceCollection<Promise<ITextEditorModel>> {

    private providers: { [scheme: string]: ITextModelContentProvider[] } = Object.create(null);
    private modelsToDispose = new Set<string>();

    constructor(
        @inject(FilesClientTypes.ITextFileEditorModel)
        private readonly textfileEditorModel: ITextFileEditorModel,
        @inject(FilesClientTypes.IFileService)
        private readonly fileService: IFileService,
        @inject(ClientTypes.IModeService)
        private readonly modeService: IModeService,
        @inject(ClientTypes.IModelService)
        private readonly modelService: IModelService,
    ) {
        super();
    }

    public async createReferencedObject(key: string, skipActivateProvider?: boolean): Promise<ITextEditorModel> {
        this.modelsToDispose.delete(key);

        const resource = URI.parse(key);

        // File or remote file provider already known
        if (this.fileService.canHandleResource(resource)) {
            return this.textfileEditorModel.load(resource, { reason: IFileLoadReason.REFERENCE as any, allowBinary: true, });
        }

        // Virtual documents
        if (this.providers[resource.scheme]) {
            console.log('This providers', this.providers)
            await this.resolveTextModelContent(key);

            return new ResourceEditorModel(resource, this.modeService, this.modelService);
        }

        // Either unknown schema, or not yet registered, try to activate
        if (!skipActivateProvider) {
            await this.fileService.activateProvider(resource.scheme);

            return this.createReferencedObject(key, true);
        }

        throw new Error('resource is not available');
    }

    public destroyReferencedObject(key: string, modelPromise: Promise<ITextEditorModel>): void {
        this.modelsToDispose.add(key);

        modelPromise.then(model => {
            if (this.modelsToDispose.has(key)) {
                // We don't create TextFileEditorModel
                // if (model instanceof TextFileEditorModel) {
                //     this.textfileEditorModel.disposeModel(model);
                // } else {
                    model.dispose();
                // }
            }
        }, err => {
            // ignore
        })
    }

    public registerTextModelContentProvider(scheme: string, provider: ITextModelContentProvider): IDisposable {
        const registry = this.providers;
        const providers = registry[scheme] || (registry[scheme] = []);
        providers.unshift(provider);

        return toDisposable(() => {
            const array = registry[scheme];

            if (!array) {
                return;
            }

            const index = array.indexOf(provider);

            if (index === -1) {
                return;
            }

            array.splice(index, 1);

            if (array.length === 0) {
                delete registry[scheme];
            }
        });
    }

    public hasTextModelContentProvider(scheme: string): boolean {
        return this.providers[scheme] !== undefined;
    }

    private async resolveTextModelContent(key: string): Promise<ITextModel> {
        const resource = URI.parse(key);
        const providers = this.providers[resource.scheme] || [];
        const factories = providers.map(p => () => Promise.resolve(p.provideTextContent(resource)));

        const model = await first(factories);
        if (!model) {
            throw new Error('resource is not available');
        }

        return model;
    }
}

@injectable()
export class TextModelResolverService implements ITextModelService {

    _serviceBrand: any;

    private resourceModelCollection: ResourceModelCollection;

    constructor(
        @inject(FilesClientTypes.IUntitledEditorService)
        private readonly untitledEditorService: IUntitledEditorService,
        @inject('Container')
        private container: Container,
        @inject(ClientTypes.IModelService)
        private readonly modelService: IModelService,
        @inject(ClientTypes.IModeService)
        private readonly modeService: IModeService,
        @inject(FilesClientTypes.ITextFileEditorModel)
        private readonly textfileEditorModel: ITextFileEditorModel,
        @inject(FilesClientTypes.IFileService)
        private readonly fileService: IFileService,
    ) { 
        this.resourceModelCollection = new ResourceModelCollection(textfileEditorModel, fileService, modeService, modelService);
    }


    public createModelReference(resource: URI) {
        return this.doCreateModelReference(resource) as any;
    }


    private async doCreateModelReference(resource: URI) {

        // untitled Schema: go through cached input
        if (resource.scheme === Schemas.untitled) {
            const model = await this.untitledEditorService.createOrGet(resource);
            //@sri TODO */
            return new ImmortalReference(model/**  as IResolvedTextEditorModel */);
        }

        // InMemory Schema: go through model service cache
        if (resource.scheme === Schemas.inMemory) {
            const cacheModel = this.modelService.getModel(resource);

            if (!cacheModel) {
                throw new Error('Cant resolve inmemory resource');
            }

            return new ImmortalReference(new ResourceEditorModel(resource, this.modeService, this.modelService));
        }

        const ref = this.resourceModelCollection.acquire(resource.toString());

        try {
            const model = await ref.object;

            return { object: model as IResolvedTextEditorModel, dispose: () => ref.dispose() };
        } catch (error) {
            ref.dispose();

            throw error;
        }
    }

    public registerTextModelContentProvider(scheme: string, provider: ITextModelContentProvider): IDisposable {
        return this.resourceModelCollection.registerTextModelContentProvider(scheme, provider);
    }

    canHandleResource(resource: URI): boolean {
		if (this.fileService.canHandleResource(resource) || resource.scheme === network.Schemas.untitled || resource.scheme === network.Schemas.inMemory) {
			return true; // we handle file://, untitled:// and inMemory:// automatically
		}

		return this.resourceModelCollection.hasTextModelContentProvider(resource.scheme);
	}
}